import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { ArrowLeft, Download, Upload, ShieldAlert, CheckCircle2, XCircle, Loader2, Trash2, FileArchive, UserX, Siren, Users2 } from "lucide-react";

const STAGE_LABELS = {
  starting: "Iniciando...",
  dumping_db: "Volcando colecciones de la base...",
  downloading_files: "Descargando archivos del storage...",
  zipping: "Empaquetando en .zip...",
  restoring_collections: "Restaurando colecciones...",
  recreating_indexes: "Recreando índices...",
  reuploading_files: "Re-subiendo archivos al storage...",
  done: "Listo",
};

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

export default function Migration() {
  const [includeFiles, setIncludeFiles] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [exportConfirmText, setExportConfirmText] = useState("");
  const [exportJobId, setExportJobId] = useState(null);
  const [exportJob, setExportJob] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);

  const [importFiles, setImportFiles] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importConfirmText, setImportConfirmText] = useState("");
  const [importJobId, setImportJobId] = useState(null);
  const [importJob, setImportJob] = useState(null);
  const fileInputRef = useRef(null);

  // Fix W (2026-09-11): CAMBIO 2 -- limpieza de referencias a supervisores
  // que ya no están activos en assigned_supervisor_ids. No borra usuarios.
  const [staleBusy, setStaleBusy] = useState(false);
  const [stalePreview, setStalePreview] = useState(null);
  const [staleConfirmText, setStaleConfirmText] = useState("");
  const [staleResult, setStaleResult] = useState(null);

  const previewStale = async () => {
    setStaleBusy(true);
    try {
      const { data } = await api.get("/admin/stale-supervisor-refs");
      setStalePreview(data);
      setStaleResult(null);
      setStaleConfirmText("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStaleBusy(false); }
  };

  const confirmCleanupStale = async () => {
    if (staleConfirmText.trim().toUpperCase() !== "LIMPIAR") return;
    setStaleBusy(true);
    try {
      const { data } = await api.post("/admin/stale-supervisor-refs/cleanup");
      setStaleResult(data);
      setStalePreview(null);
      toast.success(`Se limpiaron ${data.removed_total} referencia(s) obsoleta(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStaleBusy(false); }
  };

  // Prompt Y (2026-09-11) CAMBIO 1: objetivos cuyo sos_supervisor_id apunta a
  // una cuenta inactiva o inexistente -- se pueden reasignar acá.
  const [sosBusy, setSosBusy] = useState(false);
  const [sosStale, setSosStale] = useState(null);
  const [sosSupervisors, setSosSupervisors] = useState([]);
  const [sosPicked, setSosPicked] = useState({});
  const [sosSavingId, setSosSavingId] = useState(null);

  const previewSosStale = async () => {
    setSosBusy(true);
    try {
      const [stale, sup] = await Promise.all([
        api.get("/admin/stale-sos-refs"),
        api.get("/users/roster?role=supervisor"),
      ]);
      setSosStale(stale.data);
      setSosSupervisors(sup.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSosBusy(false); }
  };

  const reassignSos = async (clientId) => {
    const newSupId = sosPicked[clientId];
    if (!newSupId) return;
    setSosSavingId(clientId);
    try {
      await api.patch(`/clients/${clientId}`, { sos_supervisor_id: newSupId });
      setSosStale((prev) => ({ ...prev, clients: prev.clients.filter((c) => c.client_id !== clientId), total: prev.total - 1 }));
      toast.success("Supervisor del SOS reasignado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSosSavingId(null); }
  };

  // Fix AC (2026-09-18): usuarios duplicados -- MISMO NOMBRE (no email, que
  // en producción da cero coincidencias). Solo lista y da los datos para
  // decidir; la fusión/eliminación la hace el cliente a mano desde Usuarios.
  const [dupBusy, setDupBusy] = useState(false);
  const [dupGroups, setDupGroups] = useState(null);

  const previewDuplicateUsers = async () => {
    setDupBusy(true);
    try {
      const { data } = await api.get("/admin/duplicate-users");
      setDupGroups(data.groups);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDupBusy(false); }
  };

  useEffect(() => {
    if (!exportJobId) return;
    const it = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/export/${exportJobId}/status`);
        setExportJob(data);
        if (data.status === "done" || data.status === "failed") clearInterval(it);
      } catch { clearInterval(it); }
    }, 1500);
    return () => clearInterval(it);
  }, [exportJobId]);

  useEffect(() => {
    if (!importJobId) return;
    const it = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/import/${importJobId}/status`);
        setImportJob(data);
        if (data.status === "done" || data.status === "failed") clearInterval(it);
      } catch { clearInterval(it); }
    }, 1500);
    return () => clearInterval(it);
  }, [importJobId]);

  const startExport = async () => {
    if (exportConfirmText.trim().toUpperCase() !== "EXPORTAR") return;
    setExportBusy(true);
    try {
      const { data } = await api.post("/admin/export/start", { confirm: "EXPORTAR", include_files: includeFiles });
      setExportJobId(data.job_id);
      setExportJob(null);
      setExportConfirmOpen(false);
      setExportConfirmText("");
      toast.success("Exportación iniciada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExportBusy(false); }
  };

  const cleanupExport = async () => {
    if (!exportJobId) return;
    try { await api.delete(`/admin/export/${exportJobId}`); setExportJobId(null); setExportJob(null); toast.success("Paquete eliminado del servidor"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const validateImport = async () => {
    if (importFiles.length === 0) return;
    setImportBusy(true);
    try {
      const form = new FormData();
      importFiles.forEach((f) => form.append("parts", f));
      const { data } = await api.post("/admin/import/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportBusy(false); }
  };

  const confirmImport = async () => {
    if (importConfirmText.trim().toUpperCase() !== "IMPORTAR" || !importPreview) return;
    setImportBusy(true);
    try {
      const { data } = await api.post("/admin/import/confirm", { import_token: importPreview.import_token, confirm: "IMPORTAR" });
      setImportJobId(data.job_id);
      setImportJob(null);
      toast.success("Importación iniciada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportBusy(false); }
  };

  const resetImport = () => {
    setImportPreview(null);
    setImportFiles([]);
    setImportConfirmText("");
    setImportJobId(null);
    setImportJob(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1000, margin: "0 auto" }} data-testid="migration-page">
      <Link to="/admin" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14} /> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Migración / Respaldo</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 20 }}>Exportá TODO el sistema (base de datos + archivos) para mover la cuenta, o restaurá un paquete exportado anteriormente. Solo administradores.</p>

      {/* ---- EXPORT ---- */}
      <div className="card" style={{ marginBottom: 24 }} data-testid="migration-export-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Download size={20} style={{ color: "#0A0A0A", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 6px", color: "#0A0A0A" }}>Exportar todo</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
              Genera un paquete .zip con las colecciones de la base (usuarios con su contraseña actual incluida) y, opcionalmente, todos los archivos del storage (fotos, videos, documentos).
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, marginBottom: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={includeFiles} onChange={(e) => setIncludeFiles(e.target.checked)} data-testid="migration-export-include-files" />
              Incluir archivos del storage (fotos, videos, documentos) — puede tardar más y generar varias partes
            </label>

            {!exportJobId && (
              <button className="btn-primary" onClick={() => setExportConfirmOpen(true)} data-testid="migration-export-start-btn"><Download size={14} style={{ marginRight: 6 }} />Exportar todo</button>
            )}

            {exportConfirmOpen && (
              <div className="card" style={{ background: "rgba(191,52,55,0.06)", border: "1px solid #BF3437", padding: 14, marginTop: 8 }} data-testid="migration-export-confirm-box">
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10 }}>
                  <ShieldAlert size={16} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 12, color: "#0A0A0A" }}>Este paquete contiene TODOS los datos del sistema, incluidos datos personales y contraseñas encriptadas. Escribí <b>EXPORTAR</b> para confirmar.</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={exportConfirmText} onChange={(e) => setExportConfirmText(e.target.value)} placeholder="EXPORTAR" data-testid="migration-export-confirm-input" style={{ maxWidth: 180 }} />
                  <button className="btn-primary" disabled={exportConfirmText.trim().toUpperCase() !== "EXPORTAR" || exportBusy} onClick={startExport} data-testid="migration-export-confirm-submit">{exportBusy ? "Iniciando..." : "Confirmar"}</button>
                  <button className="btn-ghost" onClick={() => { setExportConfirmOpen(false); setExportConfirmText(""); }}>Cancelar</button>
                </div>
              </div>
            )}

            {exportJobId && exportJob && (
              <div style={{ marginTop: 8 }} data-testid="migration-export-progress">
                {exportJob.status === "running" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "var(--invict-text-muted)" }}>
                    <Loader2 size={14} className="spin" />
                    {STAGE_LABELS[exportJob.stage] || exportJob.stage}
                    {exportJob.stage === "downloading_files" && exportJob.total_files > 0 && ` (${exportJob.processed_files}/${exportJob.total_files})`}
                  </div>
                )}
                {exportJob.status === "failed" && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#BF3437", fontWeight: 700, fontSize: 13 }}><XCircle size={16} />Falló: {exportJob.error}</div>
                )}
                {exportJob.status === "done" && (
                  <div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#16a34a", fontWeight: 800, fontSize: 13, marginBottom: 10 }}><CheckCircle2 size={16} />Listo — {Object.keys(exportJob.counts || {}).length} colecciones{exportJob.include_files ? `, ${exportJob.file_count} archivo(s)` : ""}</div>
                    {exportJob.failed_files?.length > 0 && (
                      <div style={{ fontSize: 12, color: "#d97706", marginBottom: 10 }} data-testid="migration-export-failed-files">{exportJob.failed_files.length} archivo(s) no se pudieron descargar del storage (se registraron, no bloquearon el export).</div>
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                      {(exportJob.parts || []).map((p) => (
                        <a key={p.name} href={`${backendUrl}/api/admin/export/${exportJobId}/parts/${p.name}`} data-testid={`migration-export-part-${p.name}`}
                          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 4, background: "#0A0A0A", color: "#FFFBF4", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                          <FileArchive size={13} />{p.name} ({fmtBytes(p.size)})
                        </a>
                      ))}
                    </div>
                    <button className="btn-ghost" onClick={cleanupExport} data-testid="migration-export-cleanup-btn"><Trash2 size={12} style={{ marginRight: 6 }} />Eliminar paquete del servidor</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- IMPORT ---- */}
      <div className="card" data-testid="migration-import-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Upload size={20} style={{ color: "#0A0A0A", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 6px", color: "#0A0A0A" }}>Importar</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
              Subí TODAS las partes de un paquete exportado (.zip). Primero se valida sin escribir nada; recién al confirmar se reemplaza la base.
            </p>

            {!importPreview && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input ref={fileInputRef} type="file" accept=".zip" multiple onChange={(e) => setImportFiles(Array.from(e.target.files || []))} data-testid="migration-import-file-input" />
                <button className="btn-primary" disabled={importFiles.length === 0 || importBusy} onClick={validateImport} data-testid="migration-import-preview-btn">{importBusy ? "Validando..." : "Validar archivo"}</button>
              </div>
            )}

            {importPreview && !importJobId && (
              <div className="card" style={{ background: "var(--invict-bone-2)", padding: 14 }} data-testid="migration-import-preview-modal">
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6, color: "#0A0A0A" }}>Paquete válido</div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 8 }}>
                  Exportado el {new Date(importPreview.manifest.export_date).toLocaleString("es-AR")} · {importPreview.manifest.include_files ? "incluye archivos" : "solo base de datos"}
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--invict-bone-3)", borderRadius: 4, marginBottom: 10 }}>
                  <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: "var(--invict-bone-3)" }}><th style={{ textAlign: "left", padding: "5px 8px" }}>Colección</th><th style={{ textAlign: "right", padding: "5px 8px" }}>A importar</th><th style={{ textAlign: "right", padding: "5px 8px" }}>Actual</th></tr></thead>
                    <tbody>
                      {Object.entries(importPreview.manifest.counts || {}).map(([name, count]) => (
                        <tr key={name} style={{ borderTop: "1px solid var(--invict-bone-3)" }}>
                          <td style={{ padding: "5px 8px" }}>{name}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700 }}>{count}</td>
                          <td style={{ padding: "5px 8px", textAlign: "right", color: importPreview.current_counts[name] > 0 ? "#BF3437" : "var(--invict-text-muted)" }}>{importPreview.current_counts[name] ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.destination_has_data && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(191,52,55,0.08)", padding: 10, borderRadius: 4, marginBottom: 10 }} data-testid="migration-import-warning">
                    <ShieldAlert size={14} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
                    <div style={{ fontSize: 12, color: "#0A0A0A" }}>Esta base YA tiene datos ({importPreview.non_empty_collections.length} colecciones no vacías). Confirmar REEMPLAZA por completo esas colecciones con lo importado.</div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input value={importConfirmText} onChange={(e) => setImportConfirmText(e.target.value)} placeholder="IMPORTAR" data-testid="migration-import-confirm-input" style={{ maxWidth: 180 }} />
                  <button className="btn-primary" disabled={importConfirmText.trim().toUpperCase() !== "IMPORTAR" || importBusy} onClick={confirmImport} data-testid="migration-import-confirm-submit">{importBusy ? "Iniciando..." : "Confirmar y reemplazar"}</button>
                  <button className="btn-ghost" onClick={resetImport}>Cancelar</button>
                </div>
              </div>
            )}

            {importJobId && importJob && (
              <div style={{ marginTop: 4 }} data-testid="migration-import-progress">
                {importJob.status === "running" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "var(--invict-text-muted)" }}>
                    <Loader2 size={14} className="spin" />{STAGE_LABELS[importJob.stage] || importJob.stage}
                  </div>
                )}
                {importJob.status === "failed" && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#BF3437", fontWeight: 700, fontSize: 13 }}><XCircle size={16} />Falló: {importJob.error}. Podés volver a confirmar sin riesgo de duplicar datos.</div>
                )}
                {importJob.status === "done" && (
                  <div data-testid="migration-import-summary">
                    <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#16a34a", fontWeight: 800, fontSize: 13, marginBottom: 10 }}><CheckCircle2 size={16} />Importación completa</div>
                    <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--invict-bone-3)", borderRadius: 4, marginBottom: 10 }}>
                      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                        <thead><tr style={{ background: "var(--invict-bone-3)" }}><th style={{ textAlign: "left", padding: "5px 8px" }}>Colección</th><th style={{ textAlign: "right", padding: "5px 8px" }}>Esperado</th><th style={{ textAlign: "right", padding: "5px 8px" }}>Importado</th></tr></thead>
                        <tbody>
                          {Object.entries(importJob.summary || {}).map(([name, s]) => (
                            <tr key={name} style={{ borderTop: "1px solid var(--invict-bone-3)" }}>
                              <td style={{ padding: "5px 8px" }}>{name}</td>
                              <td style={{ padding: "5px 8px", textAlign: "right" }}>{s.expected}</td>
                              <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: s.expected === s.imported ? "#16a34a" : "#BF3437" }}>{s.imported}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {importJob.manifest?.include_files && (
                      <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 10 }}>Archivos re-subidos: {importJob.reuploaded_files}{importJob.failed_uploads?.length > 0 && <span style={{ color: "#d97706" }}> · {importJob.failed_uploads.length} fallaron (revisar storage)</span>}</div>
                    )}
                    <button className="btn-ghost" onClick={resetImport} data-testid="migration-import-reset-btn">Hacer otra importación</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- LIMPIEZA DE REFERENCIAS OBSOLETAS ---- */}
      <div className="card" style={{ marginTop: 24 }} data-testid="migration-stale-supervisors-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <UserX size={20} style={{ color: "#0A0A0A", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 6px", color: "#0A0A0A" }}>Limpiar supervisores obsoletos de los objetivos</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
              Sacá de "Supervisores asignados" los ids que ya no corresponden a un supervisor activo (cuentas desactivadas o que cambiaron de rol). No borra ni desactiva ningún usuario, solo la referencia en el objetivo.
            </p>

            {!stalePreview && !staleResult && (
              <button className="btn-primary" onClick={previewStale} disabled={staleBusy} data-testid="migration-stale-preview-btn">{staleBusy ? "Revisando..." : "Revisar referencias obsoletas"}</button>
            )}

            {stalePreview && (
              <div className="card" style={{ background: "var(--invict-bone-2)", padding: 14 }} data-testid="migration-stale-preview-box">
                {stalePreview.total_refs === 0 ? (
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", display: "flex", alignItems: "center", gap: 8 }} data-testid="migration-stale-none"><CheckCircle2 size={16} />No hay referencias obsoletas.</div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8, color: "#0A0A0A" }}>{stalePreview.total_refs} referencia(s) obsoleta(s) en {stalePreview.clients.length} objetivo(s)</div>
                    <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 10 }}>
                      {stalePreview.clients.map((c) => (
                        <div key={c.client_id} style={{ padding: "6px 0", borderTop: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`migration-stale-client-${c.client_id}`}>
                          <b>{c.client_name}</b>: {c.stale_names.join(", ")}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(191,52,55,0.08)", padding: 10, borderRadius: 4, marginBottom: 10 }}>
                      <ShieldAlert size={14} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
                      <div style={{ fontSize: 12, color: "#0A0A0A" }}>Esto NO borra ni desactiva usuarios, solo saca la referencia del objetivo. Escribí <b>LIMPIAR</b> para confirmar.</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input value={staleConfirmText} onChange={(e) => setStaleConfirmText(e.target.value)} placeholder="LIMPIAR" data-testid="migration-stale-confirm-input" style={{ maxWidth: 160 }} />
                      <button className="btn-primary" disabled={staleConfirmText.trim().toUpperCase() !== "LIMPIAR" || staleBusy} onClick={confirmCleanupStale} data-testid="migration-stale-confirm-submit">{staleBusy ? "Limpiando..." : "Confirmar limpieza"}</button>
                      <button className="btn-ghost" onClick={() => { setStalePreview(null); setStaleConfirmText(""); }}>Cancelar</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {staleResult && (
              <div className="card" style={{ background: "var(--invict-bone-2)", padding: 14 }} data-testid="migration-stale-result-box">
                <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#16a34a", fontWeight: 800, fontSize: 13, marginBottom: 8 }}><CheckCircle2 size={16} />Se sacaron {staleResult.removed_total} referencia(s) de {staleResult.clients.length} objetivo(s)</div>
                {staleResult.clients.map((c) => (
                  <div key={c.client_id} style={{ fontSize: 12, padding: "4px 0" }} data-testid={`migration-stale-result-client-${c.client_id}`}>{c.client_name}: {c.removed} sacada(s)</div>
                ))}
                <button className="btn-ghost" onClick={() => setStaleResult(null)} style={{ marginTop: 8 }} data-testid="migration-stale-result-close">Cerrar</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- SOS RUTEADO A CUENTAS INACTIVAS (Prompt Y, 2026-09-11) ---- */}
      <div className="card" style={{ marginTop: 24 }} data-testid="migration-sos-stale-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Siren size={20} style={{ color: "#BF3437", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 6px", color: "#0A0A0A" }}>SOS ruteado a cuentas inactivas</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
              Objetivos cuyo "Supervisor responsable del SOS" apunta a una cuenta que ya no está activa o fue eliminada. El ruteo real ya no cae en esa cuenta (pasa al siguiente paso de la cadena), pero conviene corregirlo para que la alerta llegue directo.
            </p>

            {!sosStale && (
              <button className="btn-primary" onClick={previewSosStale} disabled={sosBusy} data-testid="migration-sos-stale-preview-btn">{sosBusy ? "Revisando..." : "Revisar SOS mal ruteado"}</button>
            )}

            {sosStale && (
              <div className="card" style={{ background: "var(--invict-bone-2)", padding: 14 }} data-testid="migration-sos-stale-box">
                {sosStale.total === 0 ? (
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", display: "flex", alignItems: "center", gap: 8 }} data-testid="migration-sos-stale-none"><CheckCircle2 size={16} />No hay objetivos con el SOS mal ruteado.</div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8, color: "#0A0A0A" }}>{sosStale.total} objetivo(s) con el SOS apuntando a una cuenta inactiva</div>
                    <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                      {sosStale.clients.map((c) => (
                        <div key={c.client_id} style={{ padding: "8px 0", borderTop: "1px solid var(--invict-bone-3)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid={`migration-sos-stale-client-${c.client_id}`}>
                          <div style={{ fontSize: 12, minWidth: 180 }}><b>{c.client_name}</b><br /><span style={{ color: "#BF3437" }}>{c.reason}</span></div>
                          <select value={sosPicked[c.client_id] || ""} onChange={(e) => setSosPicked((p) => ({ ...p, [c.client_id]: e.target.value }))} data-testid={`migration-sos-stale-select-${c.client_id}`} style={{ maxWidth: 220 }}>
                            <option value="">Elegí un supervisor activo...</option>
                            {sosSupervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          <button className="btn-primary" disabled={!sosPicked[c.client_id] || sosSavingId === c.client_id} onClick={() => reassignSos(c.client_id)} data-testid={`migration-sos-stale-save-${c.client_id}`}>
                            {sosSavingId === c.client_id ? "Guardando..." : "Reasignar"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <button className="btn-ghost" onClick={() => setSosStale(null)} style={{ marginTop: 10 }} data-testid="migration-sos-stale-close">Cerrar</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- USUARIOS DUPLICADOS (Fix AC, 2026-09-18) ---- */}
      <div className="card" style={{ marginTop: 24 }} data-testid="migration-duplicate-users-zone">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Users2 size={20} style={{ color: "#0A0A0A", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: "0 0 6px", color: "#0A0A0A" }}>Usuarios duplicados</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 12px" }}>
              Cuentas con el MISMO NOMBRE (sin distinguir mayúsculas ni espacios de sobra) — puede ser la misma persona
              con dos cuentas por email distinto. Solo se muestra para decidir: acá no se borra ni fusiona nada, hacé
              la eliminación desde <Link to="/admin/usuarios" style={{ fontWeight: 700 }}>Usuarios</Link> (ícono de tacho) sobre la cuenta que corresponda.
            </p>

            {!dupGroups && (
              <button className="btn-primary" onClick={previewDuplicateUsers} disabled={dupBusy} data-testid="migration-duplicate-users-preview-btn">{dupBusy ? "Revisando..." : "Buscar usuarios duplicados"}</button>
            )}

            {dupGroups && (
              <div className="card" style={{ background: "var(--invict-bone-2)", padding: 14 }} data-testid="migration-duplicate-users-box">
                {dupGroups.length === 0 ? (
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a", display: "flex", alignItems: "center", gap: 8 }} data-testid="migration-duplicate-users-none"><CheckCircle2 size={16} />No se encontraron nombres duplicados.</div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, color: "#0A0A0A" }}>{dupGroups.length} nombre(s) con más de una cuenta</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {dupGroups.map((g) => (
                        <div key={g.name} style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 8 }} data-testid={`migration-duplicate-group-${g.name.replace(/\s+/g, "-")}`}>
                          <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>{g.name}</div>
                          <div style={{ maxHeight: 220, overflowY: "auto" }}>
                            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                              <thead><tr style={{ background: "var(--invict-bone-3)" }}>
                                <th style={{ textAlign: "left", padding: "5px 8px" }}>Email</th>
                                <th style={{ textAlign: "left", padding: "5px 8px" }}>Rol</th>
                                <th style={{ textAlign: "center", padding: "5px 8px" }}>Activa</th>
                                <th style={{ textAlign: "left", padding: "5px 8px" }}>Última sesión</th>
                                <th style={{ textAlign: "right", padding: "5px 8px" }}>Referencias</th>
                              </tr></thead>
                              <tbody>
                                {g.accounts.map((a) => (
                                  <tr key={a.id} style={{ borderTop: "1px solid var(--invict-bone-3)" }} data-testid={`migration-duplicate-account-${a.id}`}>
                                    <td style={{ padding: "5px 8px" }}>{a.email}</td>
                                    <td style={{ padding: "5px 8px" }}>{a.role}</td>
                                    <td style={{ padding: "5px 8px", textAlign: "center" }}>{a.active ? <CheckCircle2 size={13} color="#16a34a" style={{ display: "inline" }} /> : <XCircle size={13} color="#BF3437" style={{ display: "inline" }} />}</td>
                                    <td style={{ padding: "5px 8px" }}>{a.last_login ? new Date(a.last_login).toLocaleString("es-AR") : "Nunca"}</td>
                                    <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: a.reference_count > 0 ? "#BF3437" : "#16a34a" }}>{a.reference_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <button className="btn-ghost" onClick={() => setDupGroups(null)} style={{ marginTop: 10 }} data-testid="migration-duplicate-users-close">Cerrar</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
